import { apiClient } from '@/shared/api/client';
import type { ApiResponse } from '@/shared/types/api.types';
import { malaysiaScraping, type SsmLoginResponse } from './malaysiaScraping';
import type { OfficerAppointPayload, OfficerChangePayload, OfficerCeasePayload } from '../components/officer-notification/composables/useFormPayload';

export type { SsmLoginResponse };

/**
 * Officer Notification API
 * 이사/매니저/비서 등재/변경/해임
 *
 * Endpoints:
 * - POST /api/marketplace/v1/malaysia/officers/appoint
 * - POST /api/marketplace/v1/malaysia/officers/change
 * - POST /api/marketplace/v1/malaysia/officers/cease
 */

export interface OfficerResponse {
  success: boolean;
  error?: string;
  html?: string;
  pdf_base64?: string;
}

/** SSM officer detail returned from list-with-details endpoint */
export interface SsmOfficerDetail {
  Name: string;
  IdentificationType: string;
  IdentificationNumber: string;
  DateOfBirth: string;
  Nationality: string;
  Gender: string;
  Race: string;
  Email: string;
  PhoneNumber: string;
  Designation: string;
  BusinessOccupation: string;
  // Residential address
  ResidentialStreet01: string;
  ResidentialStreet02: string;
  ResidentialStreet03: string;
  ResidentialCity: string;
  ResidentialState: string;
  ResidentialPostCode: string;
  ResidentialCountry: string;
  // Service address
  SameAsAbove: boolean;
  ServiceStreet01?: string;
  ServiceStreet02?: string;
  ServiceStreet03?: string;
  ServiceCity?: string;
  ServiceState?: string;
  ServicePostCode?: string;
  ServiceCountry?: string;
  [key: string]: any;
}

export interface OfficersWithDetailsResponse {
  success: boolean;
  People?: SsmOfficerDetail[];
  TotalCount?: number;
  Error?: string;
}

export const officerNotificationApi = {
  /**
   * SSM 로그인 (공통 API 재사용)
   */
  ssmLogin: (credentialId: string): Promise<SsmLoginResponse> => {
    return malaysiaScraping.ssmLogin(credentialId, 'mycoid2016');
  },

  /**
   * Officer 등재 (New Director/Manager/Secretary)
   */
  appointOfficer: async (data: OfficerAppointPayload): Promise<ApiResponse<OfficerResponse>> => {
    console.log('[OfficerNotificationApi] Appoint:', {
      companyNo: data.company_no,
      officerType: data.officer_type,
      personName: data.person_name,
    });

    const response = await apiClient.post<OfficerResponse>(
      '/marketplace/v1/malaysia/officers/appoint',
      data
    );

    console.log('[OfficerNotificationApi] Appoint Response:', {
      success: (response as any)?.success || (response as any)?.data?.success
    });

    return response;
  },

  /**
   * Officer 정보 변경 (Change Particulars)
   */
  changeOfficer: async (data: OfficerChangePayload): Promise<ApiResponse<OfficerResponse>> => {
    console.log('[OfficerNotificationApi] Change:', {
      companyNo: data.company_no,
      officerType: data.officer_type,
      preIdNumber: data.pre_identification_number,
    });

    const response = await apiClient.post<OfficerResponse>(
      '/marketplace/v1/malaysia/officers/change',
      data
    );

    console.log('[OfficerNotificationApi] Change Response:', {
      success: (response as any)?.success || (response as any)?.data?.success
    });

    return response;
  },

  /**
   * SSM 에 등록된 Officer 목록 + 상세정보 조회
   */
  fetchOfficersWithDetails: async (params: {
    session_id: string;
    company_no: string;
    officer_type: string;
  }): Promise<OfficersWithDetailsResponse> => {
    console.log('[OfficerNotificationApi] fetchOfficersWithDetails:', params);
    const response = await apiClient.get<OfficersWithDetailsResponse>(
      '/marketplace/v1/malaysia/officers/list-with-details',
      { params }
    );
    const data = (response as any)?.data || response;
    console.log('[OfficerNotificationApi] fetchOfficersWithDetails Response:', {
      success: data?.success,
      count: data?.TotalCount,
    });
    return data as OfficersWithDetailsResponse;
  },

  /**
   * Officer 해임/사임 (Cessation)
   */
  ceaseOfficer: async (data: OfficerCeasePayload): Promise<ApiResponse<OfficerResponse>> => {
    console.log('[OfficerNotificationApi] Cease:', {
      companyNo: data.company_no,
      officerType: data.officer_type,
      idNumber: data.identification_number,
    });

    const response = await apiClient.post<OfficerResponse>(
      '/marketplace/v1/malaysia/officers/cease',
      data
    );

    console.log('[OfficerNotificationApi] Cease Response:', {
      success: (response as any)?.success || (response as any)?.data?.success
    });

    return response;
  },
};

export default officerNotificationApi;
